import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { CalendarOutlined, UserOutlined, StarFilled, HeartOutlined, PhoneOutlined, StarOutlined } from '@ant-design/icons';

export default function HeroSection() {
    const { t, isRTL } = useLanguage();

    const stats = [
        { value: '500+', label: t('comingSoon.hero.stats.doctors')     || 'Doctors'     },
        { value: '20k+', label: t('comingSoon.hero.stats.patients')    || 'Patients'    },
        { value: '4.9',  label: t('comingSoon.hero.stats.rating')      || 'Rating'      },
        { value: '24/7', label: t('comingSoon.hero.stats.support')     || 'Support'     },
    ];

    return (
        <section
            dir={isRTL ? 'rtl' : 'ltr'}
            className="relative font-roboto overflow-hidden bg-gradient-to-r from-secondary to-primary min-h-screen flex items-center py-4 md:py-0"
        >
            {/* Background decorative circles */}
            <div className="absolute top-10 right-10 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute bottom-10 left-10 w-80 h-80 bg-white/5 rounded-full blur-3xl" />

            <div className="relative z-10 w-full mx-auto px-4 sm:px-16 py-1 lg:px-32">
                <div className="flex flex-col-reverse lg:flex-row gap-8 md:gap-12 lg:gap-20 items-center">

                    {/* ── Content ── */}
                    <div className={`w-full lg:w-1/2 text-white space-y-6 md:space-y-8 text-center ${isRTL ? 'lg:text-right' : 'lg:text-left'}`}>

                        {/* Badge */}
                        <div className={`inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-2 text-sm font-medium`}>
                            <HeartOutlined className="text-yellow-300" />
                            <span>{t('comingSoon.hero.badge') || 'Your Trusted Health Partner'}</span>
                        </div>

                        {/* Title */}
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-roboto leading-tight">
                            {t('comingSoon.hero.title') || 'Complete Healthcare Solutions at Your Fingertips'}
                        </h1>

                        {/* Subtitle */}
                        <p className="text-base sm:text-lg md:text-xl text-white/90 leading-relaxed max-w-xl">
                            {t('comingSoon.hero.subtitle') || 'Book appointments, consult your doctor, and keep your medical records in one smart platform — with Dr Easy.'}
                        </p>

                        {/* CTA Buttons */}
                        <div className={`flex flex-wrap gap-3 justify-center ${isRTL ? 'lg:justify-end' : 'lg:justify-start'}`}>
                            <a href='#plans' className="flex items-center gap-2 bg-white text-primary font-semibold px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200">
                                <StarOutlined />
                                {t('comingSoon.hero.ctaPrimary') || 'Plans'}
                            </a>
                            <a href='#contact' className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-semibold px-6 py-3 rounded-full hover:bg-white/25 hover:scale-105 transition-all duration-200">
                                <PhoneOutlined />
                                {t('comingSoon.hero.ctaSecondary') || 'Contact Us'}
                            </a>
                        </div>

                        {/* Stats Row */}
                        <div className={`grid grid-cols-4 gap-3 pt-4 border-t border-white/20`}>
                            {stats.map((stat, i) => (
                                <div key={i} className="text-center">
                                    <p className="text-xl sm:text-2xl font-bold text-yellow-300">{stat.value}</p>
                                    <p className="text-xs sm:text-sm text-white/80 mt-0.5">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Image ── */}
                    <div className="w-full lg:w-1/2 flex justify-center lg:justify-end mt-8 lg:mt-0">
                        <div className="relative w-72 sm:w-96 md:w-[420px] lg:w-[480px] aspect-square">

                            {/* Circle bg */}
                            <div className="absolute inset-0 bg-primary/60 backdrop-blur-sm rounded-full border-4 border-white/20" />

                            {/* Doctor image */}
                            <img
                                src="/images/heroImg.png"
                                alt={t('comingSoon.hero.imageAlt') || 'Hero Image'}
                                className="absolute inset-0 w-full h-full object-contain scale-110"
                            />

                            {/* Floating card — top */}
                            <div className={`absolute -top-12 md:-top-4 ${isRTL ? '-left-9 ' : '  -right-6'} bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2 z-10`}>
                                <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <CalendarOutlined className="text-green-600 text-base" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">{t('comingSoon.hero.floatingCard1.label') || 'Next Appointment'}</p>
                                    <p className="text-sm font-bold text-gray-800">{t('comingSoon.hero.floatingCard1.value') || 'Today, 3:00 PM'}</p>
                                </div>
                            </div>

                            {/* Floating card — bottom */}
                            <div className={`absolute -bottom-4 ${isRTL ? '-left-6' : '-right-6'} bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2 z-10`}>
                                <div className="w-9 h-9 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <StarFilled className="text-yellow-500 text-base" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">{t('comingSoon.hero.floatingCard2.label') || 'Patient Rating'}</p>
                                    <p className="text-sm font-bold text-gray-800">4.9 / 5.0 ⭐</p>
                                </div>
                            </div>

                            {/* Decorative blobs */}
                            <div className="absolute -top-4 -left-4 w-20 h-20 bg-yellow-400/25 rounded-full blur-2xl animate-pulse" />
                            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-teal-400/25 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}